import { forwardRef, useId, type SelectHTMLAttributes } from "react";
import { FieldMarker } from "@/components/FieldMarker";
import {
  FIELD_CONTROL,
  FIELD_ERROR,
  FIELD_HELP,
  FIELD_LABEL,
  FIELD_LABEL_ERROR,
} from "@/components/fieldStyles";

// Labeled <select> with optional inline error and a placeholder option, the
// dropdown sibling of TextField (DRY) for the enum/lookup pickers introduced by
// BRK-S020 (relationship status, entity type, industry, CMS, servicing broker).
// Full-width and stacked for the mobile-first layout; the error is wired via
// aria-describedby + aria-invalid like TextField.
export interface SelectOption {
  value: string;
  label: string;
}

interface SelectFieldProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label: string;
  options: SelectOption[];
  error?: string;
  /** Shown as a disabled-by-default first option when set (e.g. "Select…"). */
  placeholder?: string;
  /** Show a muted "(optional)" marker (the counterpart to the required "*"). */
  optional?: boolean;
  /** A line under the label saying what the options are, as SearchSelect's
   *  (BRK-S508). */
  help?: string;
}

// REF-FORWARDING (BRK-S177): the bid-result reminder's deep link has to leave
// the cursor in the outcome control, and a caller that reached for it with
// `document.getElementById` would be addressing a shared component by an id it
// does not own. Forwarding the ref is the smaller, typed answer.
export const SelectField = forwardRef<HTMLSelectElement, SelectFieldProps>(
  function SelectField(
    { label, options, error, placeholder, id, optional, help, ...selectProps },
    ref,
  ) {
    const generatedId = useId();
    const fieldId = id ?? generatedId;
    const errorId = `${fieldId}-error`;
    const helpId = `${fieldId}-help`;
    const describedBy =
      [help && helpId, error && errorId].filter(Boolean).join(" ") || undefined;

    return (
      <div className="flex w-full flex-col gap-1 text-left">
        <span className="flex items-baseline gap-1.5">
          <label
            htmlFor={fieldId}
            className={error ? FIELD_LABEL_ERROR : FIELD_LABEL}
          >
            {label}
          </label>
          <FieldMarker required={selectProps.required} optional={optional} />
        </span>
        {help && (
          <p id={helpId} className={FIELD_HELP}>
            {help}
          </p>
        )}
        <select
          ref={ref}
          id={fieldId}
          className={FIELD_CONTROL}
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          {...selectProps}
        >
          {placeholder !== undefined && <option value="">{placeholder}</option>}
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        {error && (
          <p id={errorId} className={FIELD_ERROR}>
            {error}
          </p>
        )}
      </div>
    );
  },
);
